import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { AmountInput } from "../components/ui/amount-input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { formatINR, formatDate, formatDateTime, todayISO } from "../lib/format";
import { openAuthDownload } from "../lib/download";
import { FileDown, FileText, Plus, Pencil, Archive as ArchiveIcon } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PaymentActions } from "../components/PaymentActions";
import { EntryDetailDialog } from "../components/EntryDetailDialog";

export default function InvestorDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const nav = useNavigate();
    const [inv, setInv] = useState(null);
    const [payouts, setPayouts] = useState([]);
    const [depositOpen, setDepositOpen] = useState(false);
    const [openPay, setOpenPay] = useState(false);
    const [form, setForm] = useState({ amount: 0, payout_type: "interest", notes: "" });
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        name: "", mobile: "", address: "", principal: 0, interest_rate: 0,
        period_months: 12, start_date: todayISO(), notes: "",
    });

    async function load() {
        const [a, b] = await Promise.all([
            api.get(`/investors/${id}`),
            api.get(`/investors/${id}/payouts`),
        ]);
        setInv(a.data);
        setPayouts(b.data);
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    function openEdit() {
        if (!inv) return;
        setEditForm({
            name: inv.name || "",
            mobile: inv.mobile || "",
            address: inv.address || "",
            principal: inv.principal || 0,
            interest_rate: inv.interest_rate || 0,
            period_months: inv.period_months || 12,
            start_date: inv.start_date || todayISO(),
            notes: inv.notes || "",
        });
        setEditOpen(true);
    }

    async function saveEdit() {
        try {
            await api.put(`/investors/${id}`, editForm);
            toast.success("Investor updated");
            setEditOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function archiveInvestor() {
        const reason = prompt("Reason for archiving (optional):") ?? "";
        try {
            await api.post(`/investors/${id}/archive`, { reason });
            toast.success("Investor archived");
            nav("/investors");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function recordPayout() {
        if (!form.amount || form.amount <= 0) return toast.error("Enter amount");
        try {
            await api.post(`/investors/${id}/payouts`, form);
            toast.success("Payout recorded");
            setOpenPay(false);
            setForm({ amount: 0, payout_type: "interest", notes: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    if (!inv) return <div className="text-slate-500">Loading…</div>;
    const isAdmin = user?.role === "admin";

    return (
        <div className="space-y-5">
            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="font-display text-2xl font-bold text-slate-900">{inv.name}</h1>
                            <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                {inv.investor_code}
                            </span>
                        </div>
                        <div className="text-sm text-slate-500 mt-1">
                            {inv.mobile} · {inv.address || "—"}
                        </div>
                    </div>
                    <Badge className={inv.status === "active"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                        : "bg-slate-100 text-slate-700"}>
                        {inv.status}
                    </Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-100">
                    <Info label="Principal" value={formatINR(inv.principal)} accent />
                    <Info label="Rate p.a." value={`${inv.interest_rate}%`} />
                    <Info label="Period" value={`${inv.period_months} mo`} />
                    <Info label="Start" value={formatDate(inv.start_date)} />
                    <Info label="Maturity Date" value={formatDate(inv.maturity_date)} />
                    <Info label="Maturity Amount" value={formatINR(inv.maturity_amount)} />
                    <Info label="Paid Out" value={formatINR(inv.total_paid_out)} />
                    <Info label="Pending" value={formatINR((inv.maturity_amount || 0) - (inv.total_paid_out || 0))} />
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                    {isAdmin && (
                        <Button variant="outline" className="h-11 rounded-xl tap font-semibold"
                            data-testid="investor-edit-btn" onClick={openEdit}>
                            <Pencil className="h-4 w-4 mr-1.5" /> Edit
                        </Button>
                    )}
                    {isAdmin && !inv.archived && (
                        <Button variant="outline"
                            className="h-11 rounded-xl text-amber-700 border-amber-200 hover:bg-amber-50 tap"
                            data-testid="investor-archive-btn" onClick={archiveInvestor}>
                            <ArchiveIcon className="h-4 w-4 mr-1.5" /> Archive
                        </Button>
                    )}
                    {isAdmin && (
                        <Dialog open={openPay} onOpenChange={setOpenPay}>
                            <DialogTrigger asChild>
                                <Button data-testid="add-payout-btn" className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800 tap">
                                    <Plus className="h-4 w-4 mr-1.5" /> Record Payout
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-2xl">
                                <DialogHeader><DialogTitle>Record Payout</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <div>
                                        <Label>Amount (₹)</Label>
                                        <AmountInput data-testid="payout-amount" value={form.amount}
                                            onChange={(v) => setForm({ ...form, amount: v })}
                                            className="h-11 rounded-xl" />
                                    </div>
                                    <div>
                                        <Label>Type</Label>
                                        <Select value={form.payout_type} onValueChange={(v) => setForm({ ...form, payout_type: v })}>
                                            <SelectTrigger className="h-11 rounded-xl" data-testid="payout-type"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="interest">Interest</SelectItem>
                                                <SelectItem value="principal">Principal</SelectItem>
                                                <SelectItem value="maturity">Maturity (closes investment)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Notes</Label>
                                        <Textarea rows={2} value={form.notes}
                                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                            className="rounded-xl" />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button onClick={recordPayout} data-testid="save-payout-btn"
                                        className="bg-blue-900 hover:bg-blue-800">Save</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                    <Button variant="outline" className="h-11 rounded-xl" data-testid="inv-cert"
                        onClick={() => openAuthDownload(`/certificates/investment/${id}`)}>
                        <FileText className="h-4 w-4 mr-1.5" /> Investment Certificate
                    </Button>
                    <Button variant="outline" className="h-11 rounded-xl" data-testid="inv-passbook"
                        onClick={() => openAuthDownload(`/passbooks/investment/${id}`)}>
                        <FileDown className="h-4 w-4 mr-1.5" /> Download Passbook
                    </Button>
                    {inv.status !== "active" && (
                        <Button variant="outline" className="h-11 rounded-xl" data-testid="inv-maturity-cert"
                            onClick={() => openAuthDownload(`/certificates/investment-maturity/${id}`)}>
                            Maturity Certificate
                        </Button>
                    )}
                </div>
            </Card>

            <Card className="rounded-2xl border-slate-100">
                <div className="p-5 pb-2 font-display text-lg font-bold text-slate-900">Investment Collection</div>
                <div className="p-4 flex items-start justify-between gap-3 cursor-pointer hover:bg-slate-50"
                    role="button" tabIndex={0} data-testid="investment-deposit-open"
                    onClick={() => setDepositOpen(true)}
                    onKeyDown={(e) => { if (e.key === "Enter") setDepositOpen(true); }}>
                    <div>
                        <div className="text-xs text-slate-500">
                            {formatDateTime(inv.collection_datetime || inv.created_at)} · DEPOSIT · {inv.entered_by_name || inv.created_by || "—"}
                        </div>
                        <div className="text-sm text-slate-700 mt-0.5">Principal received · {inv.investor_code}</div>
                    </div>
                    <div className="num font-bold text-slate-900 shrink-0">{formatINR(inv.principal)}</div>
                </div>
            </Card>
            <EntryDetailDialog open={depositOpen} onClose={() => setDepositOpen(false)} entry={{
                amount: inv.principal, product: "Investment", reference: inv.investor_code,
                customer_name: inv.name, customer_code: inv.customer_code,
                collection_datetime: inv.collection_datetime || inv.created_at,
                entered_by: inv.entered_by || inv.created_by, entered_by_name: inv.entered_by_name, entered_by_role: inv.entered_by_role,
                entry_created_at: inv.entry_created_at || inv.created_at, backdated: inv.backdated,
                entry_recorded_by: inv.entry_recorded_by, entry_recorded_by_name: inv.entry_recorded_by_name, entry_recorded_by_role: inv.entry_recorded_by_role,
                collected_by: inv.collected_by, collected_by_name: inv.collected_by_name, collected_by_role: inv.collected_by_role,
                interest_rate: inv.interest_rate, maturity_date: inv.maturity_date, maturity_amount: inv.maturity_amount, start_date: inv.start_date,
                approval_status: inv.approval_status || "approved",
                approved_at: inv.approved_at || inv.created_at, approved_by: inv.approved_by || inv.created_by, approved_by_role: inv.approved_by_role,
                mode: inv.mode, notes: inv.notes, receipt_no: inv.investor_code,
                current_status: inv.archived ? "archived" : (inv.status || "active"),
            }} />

            <Card className="rounded-2xl border-slate-100">
                <div className="p-5 pb-2 font-display text-lg font-bold text-slate-900">Payout History</div>
                {payouts.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400">No payouts yet</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {payouts.slice().reverse().map((p) => (
                            <div key={p.id} className="p-4 flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-xs text-slate-500">
                                        {formatDateTime(p.payout_date)} · <span className="uppercase">{p.payout_type}</span>
                                    </div>
                                    {p.notes && <div className="text-sm text-slate-700 mt-0.5">{p.notes}</div>}
                                    {isAdmin && <PaymentActions payment={p} kind="investor_payout" onDone={load} />}
                                </div>
                                <div className="num font-bold text-slate-900 shrink-0">{formatINR(p.amount)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader><DialogTitle>Edit Investor</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <Label>Name</Label>
                                <Input data-testid="edit-inv-name" value={editForm.name}
                                    onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div>
                                <Label>Mobile</Label>
                                <Input data-testid="edit-inv-mobile" value={editForm.mobile}
                                    onChange={(e) => setEditForm((s) => ({ ...s, mobile: e.target.value.replace(/\D/g, "") }))}
                                    className="h-11 rounded-xl" inputMode="tel" />
                            </div>
                            <div>
                                <Label>Address</Label>
                                <Input data-testid="edit-inv-address" value={editForm.address}
                                    onChange={(e) => setEditForm((s) => ({ ...s, address: e.target.value }))}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div>
                                <Label>Principal (₹)</Label>
                                <AmountInput data-testid="edit-inv-principal" value={editForm.principal}
                                    onChange={(v) => setEditForm((s) => ({ ...s, principal: v }))}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div>
                                <Label>Interest Rate (% p.a.)</Label>
                                <AmountInput data-testid="edit-inv-rate" value={editForm.interest_rate}
                                    onChange={(v) => setEditForm((s) => ({ ...s, interest_rate: v }))}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div>
                                <Label>Period (months)</Label>
                                <AmountInput data-testid="edit-inv-period" value={editForm.period_months}
                                    onChange={(v) => setEditForm((s) => ({ ...s, period_months: v }))}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div>
                                <Label>Start Date</Label>
                                <Input data-testid="edit-inv-start" type="date" value={editForm.start_date}
                                    onChange={(e) => setEditForm((s) => ({ ...s, start_date: e.target.value }))}
                                    className="h-11 rounded-xl" />
                            </div>
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Textarea rows={2} value={editForm.notes}
                                onChange={(e) => setEditForm((s) => ({ ...s, notes: e.target.value }))}
                                className="rounded-xl" data-testid="edit-inv-notes" />
                        </div>
                        <div className="text-xs text-slate-500">
                            Maturity date and amount will be recomputed automatically.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                        <Button onClick={saveEdit} data-testid="save-investor-edit"
                            className="bg-blue-900 hover:bg-blue-800">
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Info({ label, value, accent }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
            <div className={`num text-base font-bold mt-0.5 ${accent ? "text-blue-900 text-lg" : "text-slate-900"}`}>
                {value}
            </div>
        </div>
    );
}
